"use client";

import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import AuthApi from "@/api/Auth";

/**
 * SessionWatcher
 *
 * Antes: al llegar `sessionExp` cerraba la sesión SIEMPRE, incluso si el usuario
 * estaba trabajando. Rastreaba actividad pero nunca la usaba.
 *
 * Ahora: si el token está por expirar y el usuario estuvo activo hace poco,
 * renueva la sesión de forma silenciosa (POST /api/auth/renew, que re-autentica
 * del lado servidor con las credenciales selladas). Si el usuario lleva rato
 * inactivo, deja que la sesión expire como antes. La renovación se permite hasta
 * un tope absoluto que impone el servidor; pasado ese punto, /api/auth/renew
 * responde 401 y aquí cerramos.
 */

// --- Parámetros de comportamiento (en milisegundos) ---
// Cada cuánto revisamos el estado de la sesión.
const POLL_MS = 15 * 1000;
// Cuánto antes del exp intentamos renovar (margen para que no falle ninguna
// petición en vuelo).
const RENEW_LEAD_MS = 2 * 60 * 1000;
// Si el usuario no interactúa en este lapso, lo consideramos ausente y NO
// renovamos: la sesión expira normalmente.
//
// ⚠️ OJO: esto corre en el NAVEGADOR. Las env NEXT_PUBLIC_* se incrustan en el
// bundle en `npm run build` (build-arg del Dockerfile / variable de CI), NO en
// el runtime del contenedor. Cambiarla en Ansible no tiene efecto: hay que
// definir NEXT_PUBLIC_RENEW_IDLE_LIMIT_MIN como variable de CI/CD en GitLab y
// reconstruir la imagen. Sin definirla, el default es 10 minutos.
const IDLE_LIMIT_MIN =
  Number(process.env.NEXT_PUBLIC_RENEW_IDLE_LIMIT_MIN) || 10;
const IDLE_LIMIT_MS = IDLE_LIMIT_MIN * 60 * 1000;

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const parts = document.cookie.split(";").map((c) => c.trim());
  const found = parts.find((c) => c.startsWith(name + "="));
  return found ? decodeURIComponent(found.split("=").slice(1).join("=")) : null;
}

function readSessionExp() {
  const raw = getCookie("sessionExp");
  const expMs = raw ? Number(raw) : NaN;
  return Number.isFinite(expMs) ? expMs : null;
}

export default function SessionWatcher() {
  const router = useRouter();
  const lastActivity = useRef(Date.now());
  const renewingRef = useRef(false);
  const pollRef = useRef(null);
  const bcRef = useRef(null);

  const doLogout = useCallback(async () => {
    try {
      await AuthApi.logout();
    } catch {
      // aunque falle, igual forzamos salida del lado UI
    } finally {
      try {
        bcRef.current?.postMessage({ type: "logout" });
      } catch {
        // ignore
      }
      router.replace("/auth/signin");
    }
  }, [router]);

  const tryRenew = useCallback(async () => {
    if (renewingRef.current) return;
    renewingRef.current = true;
    try {
      const res = await AuthApi.renew();
      if (res?.data?.success) {
        // El servidor ya re-escribió sessionExp; avisamos a otras pestañas
        // para que re-armen sus timers en vez de renovar también.
        try {
          bcRef.current?.postMessage({ type: "renewed" });
        } catch {
          // ignore
        }
        return true;
      }
      // success=false con 200 no debería pasar, pero por las dudas:
      await doLogout();
      return false;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Tope absoluto alcanzado o credenciales ya inválidas → cerrar.
        await doLogout();
      }
      // 502/red: error transitorio. No cerramos; el próximo poll reintenta
      // mientras quede margen antes del exp.
      return false;
    } finally {
      renewingRef.current = false;
    }
  }, [doLogout]);

  useEffect(() => {
    // --- multi-pestaña ---
    try {
      bcRef.current = new BroadcastChannel("auth");
      bcRef.current.onmessage = (ev) => {
        const type = ev?.data?.type;
        if (type === "logout") {
          router.replace("/auth/signin");
        }
        // "renewed" no requiere acción extra: el siguiente tick leerá el
        // sessionExp actualizado de la cookie.
      };
    } catch {
      // ignore
    }

    // --- registro de actividad ---
    const markActivity = () => {
      lastActivity.current = Date.now();
    };
    const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    events.forEach((e) =>
      window.addEventListener(e, markActivity, { passive: true }),
    );

    // --- ciclo de vigilancia ---
    const tick = () => {
      const expMs = readSessionExp();
      if (expMs == null) return; // sin sesión vigente

      const now = Date.now();
      const remaining = expMs - now;

      if (remaining <= 0) {
        // Token ya muerto.
        doLogout();
        return;
      }

      if (remaining <= RENEW_LEAD_MS) {
        const idleFor = now - lastActivity.current;
        if (idleFor <= IDLE_LIMIT_MS) {
          // Usuario activo y a punto de expirar → renovar.
          tryRenew();
        }
        // Si está inactivo, no hacemos nada: cuando remaining llegue a 0,
        // el siguiente tick cerrará la sesión.
      }
    };

    tick(); // chequeo inmediato al montar
    pollRef.current = setInterval(tick, POLL_MS);

    // También revisamos al volver el foco a la pestaña.
    const onFocus = () => tick();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      events.forEach((e) => window.removeEventListener(e, markActivity));
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      try {
        bcRef.current?.close();
      } catch {
        // ignore
      }
    };
  }, [router, doLogout, tryRenew]);

  return null;
}
